import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Chip } from "@mui/material";
import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  SearchInput,
  Table,
  useDebounce,
  useFetch,
} from "@4uhub/lib4uhub";
import MainApi from "../../../../services/mainApi.service";
import { ITicketAutomationRules } from "../../../../models/ticket-automation-rules";
import { useCallback, useEffect, useMemo, useState } from "react";
import { listAutomations } from "../../../../services/ticketautomation.service";
import { useAutomation } from "./useAutomation";

const TICKET_AUTOMATION_RULES_ROUTE =
  process.env.REACT_APP_AUTOMATIONS + "/api/v1/TicketAutomationRules/";
const ticketAutomationRulesService = new MainApi<ITicketAutomationRules>(
  TICKET_AUTOMATION_RULES_ROUTE,
);

const translationPath = "chatBot.page.automations.automationRules.";

const AutomationRulesPage = () => {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();

  const [automations, setAutomations] = useState<ISelectType[]>([]);

  const [selectedAutomation, setSelectedAutomation] =
    useState<ISelectType | null>(null);

  const { sendRequest } = useFetch(listAutomations);

  const { automationParams } = useAutomation();

  const text = useMemo(
    () => searchParams.get("searchByRule") ?? "",
    [searchParams],
  );

  const selectedAutomationId = useMemo(
    () => searchParams.get("ruleSelectedAutomationId") ?? "",
    [searchParams],
  );

  const searchBy = useDebounce(text, 500);
  const fetchAutomations = useCallback(async () => {
    const { data, success } = await sendRequest(automationParams);
    if (data && success) {
      setAutomations(data);

      const savedId = searchParams.get("ruleSelectedAutomationId");
      if (savedId) {
        const found = data.find((opt) => opt.id === savedId);
        if (found) setSelectedAutomation(found);
      }
    }
  }, [sendRequest]);

  useEffect(() => {
    fetchAutomations();
  }, [fetchAutomations]);

  const columns: GridColDef<ITicketAutomationRules>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "ticketAutomation",
      headerName: t(translationPath + "automation") || "Automation",
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "createdByUserName",
      headerName: t(translationPath + "created_by") || "Created By",
      maxWidth: 350,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.value
              ? t(translationPath + "active")
              : t(translationPath + "inactive")
          }
          color={params.value ? "success" : "error"}
        />
      ),
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<ITicketAutomationRules>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const changeHandler = useCallback(
    (value: ISelectType | null) => {
      setSelectedAutomation(value);

      const newParams = Object.fromEntries(searchParams.entries());

      if (value?.id) {
        newParams.ruleSelectedAutomationId = value.id;
      } else {
        delete newParams.ruleSelectedAutomationId;
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const changeSearchHandler = useCallback(
    (value: string | null) => {
      const v = (value ?? "").trim();

      const newParams = Object.fromEntries(searchParams.entries());

      if (v) {
        newParams.searchByRule = v;
      } else {
        delete newParams.searchByRule;
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const props = useMemo(() => {
    return {
      TicketAutomationId: selectedAutomationId || undefined,
      SearchBy: searchBy.length > 0 ? searchBy : undefined,
    };
  }, [selectedAutomationId, searchBy]);

  return (
    <Table<ITicketAutomationRules>
      showDefaultMacroFilters={false}
      service={ticketAutomationRulesService}
      columns={columns}
      title={t(translationPath + "automation_rules")}
      searchInput={false}
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      serviceProps={props}
      macroFilters={() => (
        <>
          <Box
            sx={{
              width: "30%",
              minWidth: "450px",
            }}
          >
            <SearchInput
              onSearch={changeSearchHandler}
              placeholder={t(translationPath + "search")}
              focus={false}
              cleanButton
              defaultValue={text}
              fireOnMount={false}
            />
          </Box>
          <AutoCompleteUncontrolled
            label={t(translationPath + "automation") || "Automation"}
            onValueSelected={changeHandler}
            value={selectedAutomation}
            options={automations}
            clearable={true}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
          />
        </>
      )}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Searh for Whatsapp template"
      }
      addButtonLabel={
        t(translationPath + "add_automation_rule") || "Add Automation Rule"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default AutomationRulesPage;
